import { useState } from 'react'
import { useSelectedSchool } from '@/contexts/SelectedSchoolContext'
import { SchoolSwitcher } from '@/components/school/SchoolSwitcher'
import { useInstructors } from '@/hooks/useInstructors'
import { useSchoolInstructorCalendar } from '@/hooks/useSchoolInstructorCalendar'
import { useSchoolCalendarAllInstructors, type SchoolCalendarEntry } from '@/hooks/useSchoolCalendarAllInstructors'
import { MonthGrid, type CalendarEvent } from '@/components/calendar/MonthGrid'
import { WhiteBadge } from '@/components/ui/Badge'
import { LessonCard } from '@/components/ui/LessonCard'

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-CA', {
    month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}
function formatTime(iso: string) {
  return new Date(iso).toLocaleString('en-CA', { hour: 'numeric', minute: '2-digit' })
}
function toDateKey(iso: string) {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const sectionStyle: React.CSSProperties = {
  background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14,
  padding: '20px 22px', marginBottom: 18,
}
const sectionTitleStyle: React.CSSProperties = {
  font: '600 16px Lora, serif', color: '#1B2A4A', marginBottom: 14,
}

const STATUS_LABEL: Record<string, string> = { scheduled: 'Upcoming', completed: 'Completed' }
const STATUS_COLOR: Record<string, string> = { scheduled: '#0F7C6C', completed: 'rgba(27,42,74,0.5)' }

// A small deterministic palette for telling instructors apart on the "all instructors"
// grid — same instructor always gets the same color across renders/refreshes, picked by
// hashing their id rather than render order (so it doesn't shift as lessons load in).
const INSTRUCTOR_PALETTE = ['#0F7C6C', '#1B2A4A', '#D4972B', '#6A4C93', '#1E88A8', '#C0522A']
function colorForInstructor(instructorId: string): string {
  let hash = 0
  for (let i = 0; i < instructorId.length; i++) hash = (hash * 31 + instructorId.charCodeAt(i)) | 0
  return INSTRUCTOR_PALETTE[Math.abs(hash) % INSTRUCTOR_PALETTE.length]
}
function firstName(fullName: string | null): string {
  return fullName?.split(' ')[0] ?? 'Unknown'
}

function AllInstructorsCalendar({ schoolId }: { schoolId: string }) {
  const { lessons, loading, error } = useSchoolCalendarAllInstructors(schoolId)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  const events: CalendarEvent[] = lessons.map(l => ({
    id: l.id, date: toDateKey(l.scheduledAt),
    label: `${firstName(l.instructorName)} · ${firstName(l.studentName)}`,
    color: colorForInstructor(l.instructorId),
  }))
  const visibleLessons = selectedDate ? lessons.filter(l => toDateKey(l.scheduledAt) === selectedDate) : lessons

  // Legend: one swatch per instructor who has at least one upcoming lesson, so the color
  // coding on the grid is actually readable instead of just "some colors".
  const instructorLegend = [...new Map(lessons.map(l => [l.instructorId, l.instructorName])).entries()]

  return (
    <div className='cal-layout'>
      <div className='cal-col-left'>
        {instructorLegend.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
            {instructorLegend.map(([id, name]) => (
              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: colorForInstructor(id), flexShrink: 0 }} />
                <span style={{ font: '500 12px Inter, sans-serif', color: 'rgba(27,42,74,0.6)' }}>{name ?? 'Instructor'}</span>
              </div>
            ))}
          </div>
        )}
        <MonthGrid events={events} selectedDate={selectedDate} onSelectDate={setSelectedDate} />
        {selectedDate && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <div style={{ font: '500 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)' }}>
              Showing {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <button onClick={() => setSelectedDate(null)} style={{ background: 'none', border: 'none', color: '#0F7C6C', cursor: 'pointer', font: '500 12.5px Inter, sans-serif' }}>
              Show all
            </button>
          </div>
        )}
      </div>

      <div className='cal-col-right'>
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>
            {selectedDate
              ? `Lessons on ${new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-CA', { month: 'long', day: 'numeric' })}`
              : 'Upcoming lessons'}
          </div>
          {loading && <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Loading…</div>}
          {error && <div className='banner-error'>{error}</div>}
          {!loading && !error && visibleLessons.length === 0 && (
            <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
              {selectedDate ? 'No lessons scheduled on this date.' : 'No upcoming lessons scheduled at your school.'}
            </div>
          )}
          {!loading && visibleLessons.map((l: SchoolCalendarEntry) => (
            <LessonCard
              key={l.id}
              title={l.studentName ?? 'Student'}
              subtitle={`with ${l.instructorName ?? 'Instructor'}`}
              meta={`${formatDateTime(l.scheduledAt)}${l.plannedEndAt ? ` – ${formatTime(l.plannedEndAt)}` : ''}${l.packageName ? ` · ${l.packageName}` : ''}`}
              badge={<WhiteBadge label={STATUS_LABEL[l.status] ?? l.status} />}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Read-only: the school sees a selected instructor's CONFIRMED lesson times for their own
 * assigned students only -- never the instructor's broader availability configuration or
 * any other booking. See useSchoolInstructorCalendar.ts for the RLS this relies on.
 */
export default function SchoolInstructorCalendar() {
  const { selectedSchoolId, schools, loading: schoolsLoading } = useSelectedSchool()
  const { instructors, loading: instructorsLoading } = useInstructors()
  const [view, setView] = useState<'all' | 'single'>('all')
  const [instructorId, setInstructorId] = useState('')
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const { lessons, loading, error } = useSchoolInstructorCalendar(selectedSchoolId, instructorId || null)

  const events: CalendarEvent[] = lessons.map(l => ({
    id: l.id, date: toDateKey(l.scheduledAt), label: l.studentName ?? 'Lesson',
    color: STATUS_COLOR[l.status] === 'rgba(27,42,74,0.5)' ? '#9AA5B5' : '#0F7C6C',
  }))
  const visibleLessons = selectedDate ? lessons.filter(l => toDateKey(l.scheduledAt) === selectedDate) : lessons

  if (schoolsLoading) return <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>Loading…</div>
  if (schools.length === 0) {
    return <div className='banner-error'>You need to be an active owner or admin of a school to view instructor calendars.</div>
  }

  return (
    <div>
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 20 }}>Instructor calendars</div>

      <SchoolSwitcher />

      <div style={{
        display: 'inline-flex', gap: 2, padding: 3, marginBottom: 18,
        background: 'rgba(27,42,74,0.06)', borderRadius: 10,
      }}>
        {([['all', 'All instructors'], ['single', 'By instructor']] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setView(key)}
            style={{
              padding: '8px 18px', border: 'none', borderRadius: 7,
              background: view === key ? '#fff' : 'none',
              boxShadow: view === key ? '0 1px 3px rgba(27,42,74,0.15)' : 'none',
              font: view === key ? '600 13.5px Inter, sans-serif' : '500 13.5px Inter, sans-serif',
              color: view === key ? '#0F7C6C' : 'rgba(27,42,74,0.55)', cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'all' && selectedSchoolId && <AllInstructorsCalendar schoolId={selectedSchoolId} />}

      {view === 'single' && (
      <>
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Select an instructor</div>
        <select value={instructorId} onChange={e => { setInstructorId(e.target.value); setSelectedDate(null) }} className='field-input' disabled={instructorsLoading}>
          <option value=''>Select an instructor…</option>
          {instructors.map(i => <option key={i.membershipId} value={i.userId}>{i.fullName ?? i.email}</option>)}
        </select>
        <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 8 }}>
          Shows only this instructor's confirmed lessons with students assigned to your school — never
          their availability settings or any other booking on their calendar.
        </div>
      </div>

      {error && <div className='banner-error' style={{ marginBottom: 16 }}>{error}</div>}

      {instructorId && (
        <div className='cal-layout'>
          <div className='cal-col-left'>
            <MonthGrid events={events} selectedDate={selectedDate} onSelectDate={setSelectedDate} />
            {selectedDate && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                <div style={{ font: '500 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)' }}>
                  Showing {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
                <button onClick={() => setSelectedDate(null)} style={{ background: 'none', border: 'none', color: '#0F7C6C', cursor: 'pointer', font: '500 12.5px Inter, sans-serif' }}>
                  Show all
                </button>
              </div>
            )}
          </div>

          <div className='cal-col-right'>
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Lessons</div>
              {loading && <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Loading…</div>}
              {!loading && visibleLessons.length === 0 && (
                <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>{selectedDate ? 'No lessons on this date.' : 'No lessons found for this instructor at your school.'}</div>
              )}
              {!loading && visibleLessons.map(l => {
                const meta = `${formatDateTime(l.scheduledAt)}${(l.plannedEndAt ?? l.finishedAt) ? ` – ${formatTime((l.plannedEndAt ?? l.finishedAt)!)}` : ''}`
                // Only a still-upcoming lesson gets the teal "noteworthy" treatment —
                // a completed one is history, not something to draw the eye to.
                if (l.status === 'scheduled') {
                  return (
                    <LessonCard
                      key={l.id}
                      title={l.studentName ?? 'Student'}
                      meta={meta}
                      badge={<WhiteBadge label={STATUS_LABEL[l.status]} />}
                    />
                  )
                }
                return (
                  <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
                    <div>
                      <div style={{ font: '500 14px Inter, sans-serif', color: '#1B2A4A' }}>{l.studentName ?? 'Student'}</div>
                      <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 2 }}>
                        {meta}
                      </div>
                    </div>
                    <div style={{ font: '600 12px Inter, sans-serif', color: STATUS_COLOR[l.status] ?? 'rgba(27,42,74,0.5)', flexShrink: 0 }}>
                      {STATUS_LABEL[l.status] ?? l.status}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  )
}
